import { useEffect, useState } from "react";
import { Button, Modal, Space, Typography } from "antd";
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  FormOutlined,
  AppstoreOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  CoffeeOutlined,
  MedicineBoxOutlined,
  SmileOutlined,
  SafetyCertificateOutlined,
  BarChartOutlined,
  AuditOutlined,
  GlobalOutlined,
  BulbFilled,
  InfoCircleOutlined,
  MoonOutlined,
  SunOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { AppHeaderLogo } from "../components/AppHeaderLogo";
import { GlobalUserMenu } from "../components/GlobalUserMenu";
import { useUiState } from "../state";
import {
  fetchComplianceAccess,
  fetchPlans,
  type PlanListItem,
} from "../services/compliance";
import { textMap } from "../i18n";

const { Text } = Typography;

const homeTextMap = {
  en: {
    navFeatures: "Features",
    navPricing: "Pricing",
    navDocs: "Documentation",
    navResources: "Resources",
    login: "Login",
    workspace: "Workspace",
    heroCtaChat: "Open chat",
    heroCtaStudio: "Studio console",
    heroTitle: "Publish with",
    heroAccent: "Confidence",
    heroTitleSuffix: "Not Risk",
    heroSubtitle: "Compliance-first copy review for Food, Supplement, and Cosmetic brands. XTool helps you stay aligned with FDA guidelines in seconds.",
    industryTrusted: "Industry Trusted:",
    food: "Food",
    supplement: "Supplement",
    cosmetic: "Cosmetic",
    builtWith:
      "Cites FDA (openFDA) and FTC (RSS) public data as evidence signals; expanding sources.",
    databaseV: "Database v2.4",
    lastSync: "Last sync: 2 hours ago",
    howItWorks: "How it Works",
    howItWorksDesc: "Four steps to absolute compliance certainty.",
    pasteCopy: "Paste copy",
    pasteCopyDesc: "Simply drop your marketing text or product descriptions into the editor.",
    chooseCategory: "Choose category",
    chooseCategoryDesc: "Select Food, Supplement, or Cosmetic to load specific regulatory logic.",
    runReview: "Run review",
    runReviewDesc:
      "XTool cites FDA (openFDA) and FTC (RSS) public data as contextual evidence signals, with rules and models; sources continuously expand.",
    getRewrites: "Get rewrites",
    getRewritesDesc: "Receive actionable suggestions to fix high-risk claims instantly.",
    deepAnalysis: "Deep Analysis",
    deepAnalysisDesc: "Contextual breakdown of Phrase, Reason, and Suggestion.",
    fdaContext: "FDA Context",
    fdaContextDesc:
      "Matched FDA enforcement and FTC press items as evidence signals; expanding data sources.",
    bilingualSupport: "Bilingual Support",
    bilingualSupportDesc: "Full EN/CN compliance checking for global brands.",
    uiFlexibility: "UI Flexibility",
    uiFlexibilityDesc: "Optimized Dark/Light modes for long analysis sessions.",
    complianceEngine: "Compliance isn't a checkbox. It's an engine.",
    complianceEngineDesc: "We provide more than just flagging. Our system understands the nuance between \"helps maintain\" and \"treats,\" ensuring your copy stays in the safe zone while remaining persuasive.",
    bullet1: "Real-time phrase monitoring",
    bullet2: "Historical audit logging",
    bullet3: "Multi-user workspace for teams",
    livePreview: "Live Analysis Preview",
    flaggedPhrase: "Flagged Phrase",
    medicalClaim: "Medical Claim",
    fdaProhibits: "FDA prohibits \"may cause\" in therapeutic contexts for supplements.",
    xtoolSuggestion: "XTool Suggestion",
    choosePower: "Choose Your Power",
    choosePowerDesc: "Scale your compliance from directional to automated.",
    perMonth: "/ month",
    directionalAdvice: "Directional advice on 5 scans/mo",
    basicRisk: "Basic risk scoring (High/Low)",
    noRewrites: "No one-click rewrites",
    currentPlan: "Current Plan",
    mostPopular: "Most Popular",
    oneClickRewrite: "One-click full rewrite",
    unlimitedScans: "Unlimited scans & reports",
    apiAccess: "API access for CMS integration",
    teamCollab: "Team collaboration tools",
    upgradeToPro: "Upgrade to Pro",
    faqTitle: "Frequently Asked Questions",
    faq1Q: "Is this legal advice?",
    faq1A: "XTool provides automated regulatory guidance based on public data. It is a decision-support tool and does not constitute formal legal counsel.",
    faq2Q: "What categories do you support?",
    faq2A: "We currently specialize in Food (Labeling & Claims), Dietary Supplements (DSHEA), and Cosmetics (Ingredients & Performance claims).",
    faq3Q: "How do credits work?",
    faq3A: "On the Free tier, one \"scan\" equals up to 500 words. Pro users have unlimited access with tiered rate limits for API usage.",
    faq4Q: "Can my team collaborate on reviews?",
    faq4A: "Yes, Pro accounts allow you to invite team members to shared workspaces where you can review, comment, and approve copy centrally.",
    readyTitle: "Ready to de-risk your marketing?",
    readyDesc: "Join 500+ CPG brands using XTool to accelerate their go-to-market speed without legal bottlenecks.",
    bookDemo: "Book Enterprise Demo",
    footerDesc: "Architectural Sentinel Systems. Advanced compliance intelligence for the modern CPG landscape.",
    product: "Product",
    integrations: "Integrations",
    legal: "Legal",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",
    security: "Security",
    connect: "Connect",
  },
  zh: {
    navFeatures: "功能",
    navPricing: "定价",
    navDocs: "文档",
    navResources: "资源",
    login: "登录",
    workspace: "工作台",
    heroCtaChat: "进入聊天",
    heroCtaStudio: "进入 Studio 控制台",
    heroTitle: "安心发布",
    heroAccent: "合规自信",
    heroTitleSuffix: "远离风险",
    heroSubtitle: "XTool 专业合规文案审查，覆盖食品、保健品、化妆品。秒级对标 FDA 指南，助您安心上架。",
    industryTrusted: "行业信任:",
    food: "食品",
    supplement: "保健品",
    cosmetic: "化妆品",
    builtWith: "引用 FDA（openFDA）与 FTC（官方 RSS）等公开数据作证据提示，持续扩充数据源。",
    databaseV: "数据库 v2.4",
    lastSync: "最近同步: 2 小时前",
    howItWorks: "如何运作",
    howItWorksDesc: "四步实现合规确定性。",
    pasteCopy: "粘贴文案",
    pasteCopyDesc: "将营销文案或产品描述粘贴到编辑器中。",
    chooseCategory: "选择品类",
    chooseCategoryDesc: "选择食品、保健品或化妆品，加载对应监管逻辑。",
    runReview: "执行审查",
    runReviewDesc:
      "引用 FDA（openFDA）与 FTC（RSS）等公开数据作证据提示，结合规则与模型审查；数据源持续扩充。",
    getRewrites: "获取改写",
    getRewritesDesc: "即时获得可执行的高风险声明修正建议。",
    deepAnalysis: "深度分析",
    deepAnalysisDesc: "短语、原因、建议的上下文拆解。",
    fdaContext: "FDA 依据",
    fdaContextDesc: "展示与宣称相关的 FDA/FTC 公开记录摘要与来源链接。",
    bilingualSupport: "双语支持",
    bilingualSupportDesc: "完整的英/中合规检测，助力跨境品牌。",
    uiFlexibility: "界面灵活",
    uiFlexibilityDesc: "优化深色/浅色模式，适合长时间分析。",
    complianceEngine: "合规不是打勾，而是引擎。",
    complianceEngineDesc: "不止于标记风险。系统理解「有助于维持」与「治疗」等措辞的微妙差异，让文案既合规又具说服力。",
    bullet1: "实时短语监测",
    bullet2: "历史审计日志",
    bullet3: "团队多人协作空间",
    livePreview: "实时分析预览",
    flaggedPhrase: "标注短语",
    medicalClaim: "医疗声称",
    fdaProhibits: "FDA 禁止在保健品语境中使用「may cause」进行疗效表述。",
    xtoolSuggestion: "XTool 建议",
    choosePower: "选择方案",
    choosePowerDesc: "从定向建议到自动化，随需扩展。",
    perMonth: "/ 月",
    directionalAdvice: "每月 5 次定向建议",
    basicRisk: "基础风险评分（高/低）",
    noRewrites: "无一键改写",
    currentPlan: "当前方案",
    mostPopular: "最受欢迎",
    oneClickRewrite: "一键全文改写",
    unlimitedScans: "无限扫描与报告",
    apiAccess: "API 接入 CMS",
    teamCollab: "团队协作工具",
    upgradeToPro: "升级 Pro",
    faqTitle: "常见问题",
    faq1Q: "这是法律建议吗？",
    faq1A: "XTool 基于公开数据提供自动化监管指导，属于决策支持工具，不构成正式法律意见。",
    faq2Q: "支持哪些品类？",
    faq2A: "目前专注食品（标签与宣称）、膳食补充剂（DSHEA）、化妆品（成分与功效宣称）。",
    faq3Q: "点数如何计算？",
    faq3A: "免费版每次扫描约 500 词。Pro 用户无限使用，API 有分层限速。",
    faq4Q: "团队可以协作吗？",
    faq4A: "可以。Pro 账户可邀请成员到共享工作区，集中审查、评论与审批文案。",
    readyTitle: "准备好为营销降险了吗？",
    readyDesc: "加入 500+ 消费品品牌，用 XTool 加速上市，规避法律瓶颈。",
    bookDemo: "预约企业演示",
    footerDesc: "架构哨兵系统。面向现代消费品行业的专业合规审查。",
    product: "产品",
    integrations: "集成",
    legal: "法律",
    privacyPolicy: "隐私政策",
    termsOfService: "服务条款",
    security: "安全",
    connect: "联系",
  },
} as const;

const getHowItWorks = (lang: "en" | "zh") => [
  { icon: FormOutlined, title: homeTextMap[lang].pasteCopy, desc: homeTextMap[lang].pasteCopyDesc },
  { icon: AppstoreOutlined, title: homeTextMap[lang].chooseCategory, desc: homeTextMap[lang].chooseCategoryDesc },
  { icon: ThunderboltOutlined, title: homeTextMap[lang].runReview, desc: homeTextMap[lang].runReviewDesc },
  { icon: BulbOutlined, title: homeTextMap[lang].getRewrites, desc: homeTextMap[lang].getRewritesDesc },
];

const getFeatureCards = (lang: "en" | "zh") => [
  { icon: BarChartOutlined, title: homeTextMap[lang].deepAnalysis, desc: homeTextMap[lang].deepAnalysisDesc },
  { icon: AuditOutlined, title: homeTextMap[lang].fdaContext, desc: homeTextMap[lang].fdaContextDesc },
  { icon: GlobalOutlined, title: homeTextMap[lang].bilingualSupport, desc: homeTextMap[lang].bilingualSupportDesc },
  { icon: BulbFilled, title: homeTextMap[lang].uiFlexibility, desc: homeTextMap[lang].uiFlexibilityDesc },
];

const getFeatureBullets = (lang: "en" | "zh") => [
  homeTextMap[lang].bullet1,
  homeTextMap[lang].bullet2,
  homeTextMap[lang].bullet3,
];

const getFaqItems = (lang: "en" | "zh") => [
  { q: homeTextMap[lang].faq1Q, a: homeTextMap[lang].faq1A },
  { q: homeTextMap[lang].faq2Q, a: homeTextMap[lang].faq2A },
  { q: homeTextMap[lang].faq3Q, a: homeTextMap[lang].faq3A },
  { q: homeTextMap[lang].faq4Q, a: homeTextMap[lang].faq4A },
];

export function HomePage() {
  const { lang, themeMode, toggleLang, toggleTheme, authed } = useUiState();
  const navigate = useNavigate();
  const [modal, modalContextHolder] = Modal.useModal();
  const [plans, setPlans] = useState<PlanListItem[]>([]);

  useEffect(() => {
    let mounted = true;
    fetchPlans().then((planRows) => {
      if (!mounted) return;
      setPlans(planRows);
    });
    return () => { mounted = false; };
  }, []);

  const gt = textMap[lang];

  const handleStartChat = () => {
    if (authed) {
      navigate("/chat");
      return;
    }
    modal.confirm({
      title: lang === "zh" ? "需要先登录" : "Login Required",
      content: lang === "zh" ? "进入聊天前请先登录账号。" : "Please sign in to open chat.",
      okText: lang === "zh" ? "去登录" : "Login now",
      cancelText: lang === "zh" ? "稍后" : "Later",
      onOk: () => navigate("/login"),
    });
  };

  const handleEnterStudio = async () => {
    if (!authed) {
      modal.confirm({
        title: lang === "zh" ? "需要先登录" : "Login Required",
        content:
          lang === "zh"
            ? "Studio 控制台需要登录后使用。"
            : "Please sign in to open Studio.",
        okText: lang === "zh" ? "去登录" : "Login now",
        cancelText: lang === "zh" ? "稍后" : "Later",
        onOk: () => navigate("/login"),
      });
      return;
    }
    const access = await fetchComplianceAccess();
    if (!access?.can_use_studio) {
      modal.confirm({
        title: gt.complianceHubLockedTitle,
        content: gt.complianceHubLockedLead,
        okText: gt.upgradePlanNow,
        cancelText: gt.later,
        onOk: () => navigate("/app/upgrade"),
      });
      return;
    }
    navigate("/studio");
  };

  const freePlan = plans.find((p) => p.code.toUpperCase().includes("FREE"));
  const proPlan = plans.find((p) => p.code.toUpperCase().includes("PRO"));
  const t = homeTextMap[lang];

  return (
    <div className={`marketing-home ${themeMode === "dark" ? "marketing-home-dark" : ""}`}>
      {modalContextHolder}

      {/* TopNavBar - design lines 100-124 */}
      <nav className="mk-topnav">
        <div className="mk-nav-inner">
          <div className="mk-logo-row">
            <AppHeaderLogo />
            <Text className="mk-logo">XTool</Text>
          </div>
          <div className="mk-links">
            <a href="#how-it-works">{t.navFeatures}</a>
            <a href="#pricing">{t.navPricing}</a>
            <a href="#faq">{t.navDocs}</a>
            <a href="#footer">{t.navResources}</a>
          </div>
          <Space size="middle">
            <Button
              type="text"
              size="small"
              className="icon-btn-pill icon-btn-pill-circle"
              icon={themeMode === "dark" ? <SunOutlined /> : <MoonOutlined />}
              onClick={() => toggleTheme(themeMode !== "dark")}
            />
            <Button
              type="text"
              className="icon-btn-pill"
              icon={<GlobalOutlined />}
              onClick={toggleLang}
            >
              EN / 中文
            </Button>
            <Button type="text" className="mk-nav-btn" onClick={() => navigate(authed ? "/app/dashboard" : "/login")}>
              {authed ? "Workspace" : "Login"}
            </Button>
            <GlobalUserMenu />
          </Space>
        </div>
      </nav>

      {/* Hero - design lines 125-185 */}
      <header className="mk-hero">
        <div className="mk-hero-inner">
          <div>
            <h1 className="mk-hero-title">
              {t.heroTitle} <span className="mk-hero-accent">{t.heroAccent}</span>, {t.heroTitleSuffix}
            </h1>
            <p className="mk-hero-subtitle">
              {t.heroSubtitle}
            </p>
            <div className="mk-hero-cta">
              <Button type="primary" size="large" className="mk-cta-primary" onClick={handleStartChat}>
                {t.heroCtaChat} <ArrowRightOutlined />
              </Button>
              <Button
                size="large"
                className="mk-cta-secondary mk-cta-studio"
                onClick={() => void handleEnterStudio()}
              >
                <span>{t.heroCtaStudio}</span>
                <span className="mk-cta-pro-pill">Pro</span>
              </Button>
            </div>
            <div className="mk-industry-trusted">
              <span className="mk-industry-label">{t.industryTrusted}</span>
              <div className="mk-industry-tags">
                <span className="mk-industry-tag"><CoffeeOutlined /> {t.food}</span>
                <span className="mk-industry-tag"><MedicineBoxOutlined /> {t.supplement}</span>
                <span className="mk-industry-tag"><SmileOutlined /> {t.cosmetic}</span>
              </div>
            </div>
          </div>
          <div className="mk-preview-wrap">
            <div className="mk-preview-blur" />
            <div className="mk-preview-card-inner">
              <img
                className="mk-preview-img"
                src="https://lh3.googleusercontent.com/aida-public/AB6AXuB1RA3sy2gIwe_YEEu5WEc4mXyOZuGQ_aTC9XwZZpEGNes7BnKYExwBw5_GXU3Qd3BWhUPm8n5m36ttN50VwNJpBssJ1IEtUI21ft2kAxtigNWxJWA3fS04ZZ-zM_guj-iwME3VA277rkMCvEDR4t1AgpOWiBmiyvrY54pvWoP2on_vnB_Me98i4Z_TTqNWKxJNyiAovPvw_FEhZ1MAG3tyGnQNkQ7C-EGVakaWIfSbPwtvdPZi_RaWCU5LfO0hGg9Y5IUdW4cqlbV0"
                alt="XTool Dashboard Preview"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Authority Strip - design lines 188-206 */}
      <section className="mk-strip">
        <div className="mk-strip-inner">
          <div className="mk-strip-left">
            <SafetyCertificateOutlined className="mk-strip-icon" />
            <span>{t.builtWith}</span>
          </div>
          <div className="mk-strip-right">
            <span className="mk-strip-db">
              <span className="mk-strip-pulse" />
              {t.databaseV}
            </span>
            <span className="mk-strip-divider">|</span>
            <span>{t.lastSync}</span>
          </div>
        </div>
      </section>

      {/* How it Works - design lines 208-253 */}
      <section id="how-it-works" className="mk-section mk-how">
        <div className="mk-section-header">
          <h2 className="mk-section-title">{t.howItWorks}</h2>
          <p className="mk-section-desc">{t.howItWorksDesc}</p>
        </div>
        <div className="mk-grid4">
          {getHowItWorks(lang).map((item) => (
            <div className="mk-card" key={item.title}>
              <div className="mk-card-icon-wrap">
                <item.icon className="mk-card-icon" />
              </div>
              <h3 className="mk-card-title">{item.title}</h3>
              <p className="mk-card-desc">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Feature Highlights - design lines 255-294 */}
      <section className="mk-section mk-feature">
        <div className="mk-feature-inner">
          <div className="mk-feature-cards">
            {getFeatureCards(lang).map((item) => (
              <div className="mk-mini-card" key={item.title}>
                <item.icon className="mk-mini-icon" />
                <h4 className="mk-mini-title">{item.title}</h4>
                <p className="mk-mini-desc">{item.desc}</p>
              </div>
            ))}
          </div>
          <div className="mk-feature-content">
            <h2 className="mk-feature-title">{t.complianceEngine}</h2>
            <p className="mk-feature-desc">
              {t.complianceEngineDesc}
            </p>
            <ul className="mk-feature-list">
              {getFeatureBullets(lang).map((item) => (
                <li key={item}>
                  <CheckCircleOutlined className="mk-feature-check" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Preview Mock Card - design lines 296-339 */}
      <section className="mk-section mk-preview-section">
        <div className="mk-preview-container">
          <div className="mk-preview-card-outer">
            <div className="mk-preview-card-inner-white">
              <div className="mk-preview-head">
                <div>
                  <p className="mk-kicker">Live Analysis Preview</p>
                  <h4 className="mk-preview-product">Immunity Booster Pro 500</h4>
                </div>
                <div className="mk-preview-score-wrap">
                  <div className="mk-preview-score">85<span className="mk-score-suffix">/100</span></div>
                  <span className="mk-risk-badge">Risk: High</span>
                </div>
              </div>
              <div className="mk-flag-block">
                <div className="mk-flag-head">
                  <span className="mk-flag-label">{t.flaggedPhrase}</span>
                  <span className="mk-flag-tag">{t.medicalClaim}</span>
                </div>
                <p className="mk-flag-text">
                  &quot;...this supplement <mark className="mk-flag-highlight">may cause</mark> a reduction in inflammation...&quot;
                </p>
                <div className="mk-flag-info">
                  <InfoCircleOutlined />
                  <span>{t.fdaProhibits}</span>
                </div>
                <div className="mk-suggestion-block">
                  <div className="mk-suggestion-head">
                    <BulbOutlined />
                    <span>{t.xtoolSuggestion}</span>
                  </div>
                  <p className="mk-suggestion-text">
                    &quot;...this supplement <strong>supports</strong> healthy inflammatory response...&quot;
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pro Upgrade Comparison - design lines 341-351 */}
      <section id="pricing" className="mk-section mk-pricing">
        <div className="mk-section-header mk-pricing-header">
          <h2 className="mk-section-title">{t.choosePower}</h2>
          <p className="mk-section-desc">{t.choosePowerDesc}</p>
        </div>
        <div className="mk-pricing-grid">
          <div className="mk-price-card">
            <h3 className="mk-price-name">{freePlan?.name ?? "Free"}</h3>
            <div className="mk-price-amount">
              ${((freePlan?.price_usd_cents ?? 0) / 100).toFixed(0)} <span className="mk-price-unit">{t.perMonth}</span>
            </div>
            <ul className="mk-price-features">
              <li><CheckCircleOutlined /> {t.directionalAdvice}</li>
              <li><CheckCircleOutlined /> {t.basicRisk}</li>
              <li className="mk-price-strike"><CloseOutlined /> {t.noRewrites}</li>
            </ul>
            <Button block className="mk-price-btn-current">{t.currentPlan}</Button>
          </div>
          <div className="mk-price-card mk-price-pro">
            <span className="mk-price-badge">{t.mostPopular}</span>
            <h3 className="mk-price-name">{proPlan?.name ?? "Pro"}</h3>
            <div className="mk-price-amount">
              ${((proPlan?.price_usd_cents ?? 14900) / 100).toFixed(0)} <span className="mk-price-unit">{t.perMonth}</span>
            </div>
            <ul className="mk-price-features">
              <li><CheckCircleOutlined /> {t.oneClickRewrite}</li>
              <li><CheckCircleOutlined /> {t.unlimitedScans}</li>
              <li><CheckCircleOutlined /> {t.apiAccess}</li>
              <li><CheckCircleOutlined /> {t.teamCollab}</li>
            </ul>
            <Button block type="primary" className="mk-price-btn-pro" onClick={() => navigate("/app/upgrade")}>
              {t.upgradeToPro}
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ - design lines 354-379 */}
      <section id="faq" className="mk-section mk-faq-section">
        <h2 className="mk-faq-title">{t.faqTitle}</h2>
        <div className="mk-faq-grid">
          {getFaqItems(lang).map((item) => (
            <div className="mk-faq-item" key={item.q}>
              <h4 className="mk-faq-q">{item.q}</h4>
              <p className="mk-faq-a">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA - design lines 381-401 */}
      <section className="mk-final-cta">
        <div className="mk-final-inner">
          <div className="mk-final-blur mk-final-blur-tl" />
          <div className="mk-final-blur mk-final-blur-br" />
          <div className="mk-final-content">
            <h2 className="mk-final-title">{t.readyTitle}</h2>
            <p className="mk-final-desc">
              {t.readyDesc}
            </p>
            <div className="mk-final-btns">
              <Button size="large" className="mk-final-btn-white" onClick={handleStartChat}>
                {t.heroCtaChat}
              </Button>
              <Button
                size="large"
                className="mk-final-btn-outline mk-final-btn-studio"
                onClick={() => void handleEnterStudio()}
              >
                <span>{t.heroCtaStudio}</span>
                <span className="mk-cta-pro-pill mk-cta-pro-pill--on-dark">Pro</span>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - design lines 404-454 */}
      <footer id="footer" className="mk-footer">
        <div className="mk-footer-grid">
          <div className="mk-footer-brand">
            <div className="mk-footer-logo">XTool AI</div>
            <p className="mk-footer-desc">{t.footerDesc}</p>
            <p className="mk-footer-copy">© 2024 XTool AI. All rights reserved.</p>
          </div>
          <div className="mk-footer-col">
            <h5>{t.product}</h5>
            <ul>
              <li><a href="#how-it-works">{t.navFeatures}</a></li>
              <li><a href="#">{t.integrations}</a></li>
              <li><a href="#pricing">{t.navPricing}</a></li>
            </ul>
          </div>
          <div className="mk-footer-col">
            <h5>{t.legal}</h5>
            <ul>
              <li><a href="#">{t.privacyPolicy}</a></li>
              <li><a href="#">{t.termsOfService}</a></li>
              <li><a href="#">{t.security}</a></li>
            </ul>
          </div>
          <div className="mk-footer-col">
            <h5>{t.connect}</h5>
            <ul>
              <li><a href="#">LinkedIn</a></li>
              <li><a href="#">Twitter</a></li>
              <li><a href="#">Status</a></li>
            </ul>
          </div>
        </div>
      </footer>
    </div>
  );
}
